import type { AudioItem, TimelineItem } from '@/types/timeline';

export type SceneCompactionRequest = {
  itemId?: string;
  sceneId?: string;
  sceneIndex?: number;
  targetDurationInFrames: number;
  leadingShiftFrames?: number;
};

type SceneGroup = {
  token: string;
  sceneIndex: number;
  anchorFrom: number;
  oldFrom: number;
  oldTo: number;
  items: TimelineItem[];
  sceneItemId?: string;
  primaryVoiceoverId?: string;
};

const SCENE_LABEL_RE = /^Scene\s+(\d+)/i;

function getSceneIndexFromItem(item: Partial<TimelineItem> & { sceneId?: string; sceneIndex?: number }): number | null {
  if (Number.isFinite(Number(item.sceneIndex))) {
    return Number(item.sceneIndex);
  }

  const label = String(item.label || '');
  const match = label.match(SCENE_LABEL_RE);
  if (match) {
    return Math.max(0, Number(match[1]) - 1);
  }

  const sceneId = String(item.sceneId || '');
  const sceneIdMatch = sceneId.match(/^scene-(\d+)$/i);
  if (sceneIdMatch) {
    return Math.max(0, Number(sceneIdMatch[1]) - 1);
  }

  return null;
}

function getSceneTokenFromItem(item: Partial<TimelineItem> & { sceneId?: string; sceneIndex?: number }): string | null {
  const sceneId = typeof item.sceneId === 'string' && item.sceneId.trim() ? item.sceneId.trim() : '';
  if (sceneId) return sceneId;

  const sceneIndex = getSceneIndexFromItem(item);
  if (sceneIndex !== null) return `scene-${sceneIndex + 1}`;

  return null;
}

function isBackgroundItem(item: TimelineItem): boolean {
  return String(item.trackId || '').startsWith('background');
}

function isSceneAnchor(item: TimelineItem): boolean {
  return SCENE_LABEL_RE.test(String(item.label || '')) && !isBackgroundItem(item);
}

function isVoiceoverItem(item: TimelineItem): item is AudioItem {
  return item.type === 'audio' && String(item.label || '').trim().toLowerCase() === 'voiceover';
}

function isCaptionItem(item: TimelineItem): boolean {
  return item.type === 'text' && String(item.trackId || '') === 'caption-track';
}

function buildSceneGroups(items: TimelineItem[]): SceneGroup[] {
  const timelineItems = items.map((item) => ({ ...item }));
  const anchors = timelineItems
    .filter(isSceneAnchor)
    .map((item) => ({
      token: getSceneTokenFromItem(item) || item.id,
      sceneIndex: getSceneIndexFromItem(item) ?? Number.MAX_SAFE_INTEGER,
      anchorFrom: item.from,
      sceneItemId: item.id,
    }))
    .sort((a, b) => {
      if (a.sceneIndex !== b.sceneIndex) return a.sceneIndex - b.sceneIndex;
      return a.anchorFrom - b.anchorFrom;
    });

  if (anchors.length === 0) {
    const voiceoverAnchors = timelineItems
      .filter(isVoiceoverItem)
      .map((item, index) => ({
        token: getSceneTokenFromItem(item) || `scene-${index + 1}`,
        sceneIndex: getSceneIndexFromItem(item) ?? index,
        anchorFrom: item.from,
        sceneItemId: undefined as string | undefined,
      }))
      .sort((a, b) => {
        if (a.sceneIndex !== b.sceneIndex) return a.sceneIndex - b.sceneIndex;
        return a.anchorFrom - b.anchorFrom;
      });

    if (voiceoverAnchors.length === 0) return [];
    anchors.push(...voiceoverAnchors);
  }

  const groups = anchors.map((anchor, index) => {
    const nextAnchor = anchors[index + 1];
    return {
      token: anchor.token,
      sceneIndex: anchor.sceneIndex,
      anchorFrom: anchor.anchorFrom,
      oldFrom: anchor.anchorFrom,
      oldTo: nextAnchor ? nextAnchor.anchorFrom : Number.POSITIVE_INFINITY,
      items: [] as TimelineItem[],
      sceneItemId: anchor.sceneItemId,
      primaryVoiceoverId: undefined as string | undefined,
    };
  });

  const groupByToken = new Map(groups.map((group) => [group.token, group]));

  timelineItems.forEach((item) => {
    if (isBackgroundItem(item)) return;

    const explicitToken = getSceneTokenFromItem(item);
    if (explicitToken && groupByToken.has(explicitToken)) {
      groupByToken.get(explicitToken)!.items.push(item);
      return;
    }

    const containingGroup = groups.find((group) => item.from >= group.oldFrom && item.from < group.oldTo);
    if (containingGroup) {
      containingGroup.items.push(item);
    }
  });

  groups.forEach((group, index) => {
    group.items.sort((a, b) => a.from - b.from);
    const maxEnd = group.items.reduce((max, item) => Math.max(max, item.from + Math.max(1, item.durationInFrames || 1)), group.oldFrom + 1);
    const nextGroup = groups[index + 1];
    group.oldTo = nextGroup ? nextGroup.oldFrom : maxEnd;
    const primaryVoiceover = group.items.find(isVoiceoverItem);
    group.primaryVoiceoverId = primaryVoiceover?.id;
  });

  return groups;
}

export function compactTimelineScenes(
  items: TimelineItem[],
  requests: SceneCompactionRequest[],
): { items: TimelineItem[]; changedItemIds: Set<string> } {
  if (items.length === 0 || requests.length === 0) {
    return { items, changedItemIds: new Set() };
  }

  const groups = buildSceneGroups(items);
  if (groups.length === 0) {
    return { items, changedItemIds: new Set() };
  }

  const nextItems = items.map((item) => ({ ...item }));
  const nextItemMap = new Map(nextItems.map((item) => [item.id, item]));
  const requestMap = new Map<string, SceneCompactionRequest>();

  requests.forEach((request) => {
    let token = typeof request.sceneId === 'string' && request.sceneId.trim()
      ? request.sceneId.trim()
      : Number.isFinite(Number(request.sceneIndex))
        ? `scene-${Number(request.sceneIndex) + 1}`
        : '';

    if (!token && request.itemId) {
      const directGroup = groups.find((group) => group.items.some((item) => item.id === request.itemId));
      if (directGroup) token = directGroup.token;
    }

    if (token) requestMap.set(token, request);
  });

  const changedItemIds = new Set<string>();
  const oldTimelineEnd = groups[groups.length - 1]?.oldTo ?? 0;
  let cursor = groups[0]?.oldFrom ?? 0;

  groups.forEach((group) => {
    const request = requestMap.get(group.token);
    const targetVoiceoverId = request?.itemId || group.primaryVoiceoverId;
    const targetVoiceover = targetVoiceoverId ? nextItemMap.get(targetVoiceoverId) : undefined;
    const targetDuration = Math.max(
      1,
      Number(request?.targetDurationInFrames)
        || Number(targetVoiceover?.durationInFrames)
        || Number(group.items.find((item) => item.id === group.sceneItemId)?.durationInFrames)
        || Math.max(1, group.oldTo - group.oldFrom),
    );
    const leadingShiftFrames = Math.max(0, Number(request?.leadingShiftFrames) || 0);
    const newWindowEnd = cursor + targetDuration;

    group.items.forEach((originalItem) => {
      const item = nextItemMap.get(originalItem.id);
      if (!item) return;

      if (item.id === group.sceneItemId) {
        item.from = cursor;
        item.durationInFrames = targetDuration;
        changedItemIds.add(item.id);
        return;
      }

      if (targetVoiceoverId && item.id === targetVoiceoverId) {
        item.from = cursor;
        item.durationInFrames = targetDuration;
        changedItemIds.add(item.id);
        return;
      }

      const relativeFrom = Math.max(0, originalItem.from - group.oldFrom - leadingShiftFrames);
      item.from = Math.min(newWindowEnd - 1, cursor + relativeFrom);
      item.durationInFrames = Math.max(
        1,
        Math.min(Math.max(1, originalItem.durationInFrames || 1), newWindowEnd - item.from),
      );
      changedItemIds.add(item.id);
    });

    cursor = newWindowEnd;
  });

  const compactedWindows = new Map(
    groups.map((group) => {
      const sceneItem = group.sceneItemId ? nextItemMap.get(group.sceneItemId) : undefined;
      const voiceoverItem = group.primaryVoiceoverId ? nextItemMap.get(group.primaryVoiceoverId) : undefined;
      const from = sceneItem?.from ?? voiceoverItem?.from ?? group.anchorFrom;
      const duration = sceneItem?.durationInFrames ?? voiceoverItem?.durationInFrames ?? 1;
      return [group.token, { from, to: from + Math.max(1, duration) }] as const;
    })
  );

  const captionItems = nextItems
    .filter(isCaptionItem)
    .sort((a, b) => a.from - b.from);

  let nextCaptionFrom = 0;
  captionItems.forEach((caption) => {
    const token = getSceneTokenFromItem(caption);
    const window = token ? compactedWindows.get(token) : undefined;
    const sceneStart = window?.from ?? 0;
    const sceneEnd = window?.to ?? Number.POSITIVE_INFINITY;

    caption.from = Math.max(sceneStart, nextCaptionFrom, caption.from);
    if (caption.from >= sceneEnd) {
      caption.durationInFrames = 1;
      caption.from = sceneEnd - 1;
    }

    caption.durationInFrames = Math.max(
      1,
      Math.min(
        Math.max(1, caption.durationInFrames || 1),
        Math.max(1, sceneEnd - caption.from)
      )
    );

    nextCaptionFrom = caption.from + caption.durationInFrames;
    changedItemIds.add(caption.id);
  });

  const timelineDelta = cursor - oldTimelineEnd;
  if (timelineDelta !== 0) {
    const groupedIds = new Set(groups.flatMap((group) => group.items.map((item) => item.id)));
    nextItems.forEach((item) => {
      if (groupedIds.has(item.id)) return;
      if (item.from >= oldTimelineEnd) {
        item.from += timelineDelta;
        changedItemIds.add(item.id);
      }
    });
  }

  return { items: nextItems, changedItemIds };
}
